import { useState, useMemo } from "react";
import { Edit, Trash2, Power, PowerOff } from "lucide-react";
import styles from "./PaymentMethods.module.css";
import SelectBox from "../../../../components/BaseComponents/SelectBox/SelectBox";
import AddEditPaymentMethodModal from "../../../../components/Finance/Payments/AddEditPaymentMethodModal";

const PaymentMethods = () => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [mode, setMode] = useState("add");
  const [selectedMethod, setSelectedMethod] = useState(null);

  const [paymentMethods] = useState([
    {
      id: 1,
      name: "Bank Transfer",
      type: "Bank",
      status: "Active",
      description: "Standard SEPA bank transfer",
      usageCount: 245,
      createdAt: "2024-01-15",
      updatedAt: "2024-03-10",
    },
    {
      id: 2,
      name: "SEPA Direct Debit",
      type: "Bank",
      status: "Active",
      description: "Automated SEPA debit collection",
      usageCount: 189,
      createdAt: "2024-01-15",
      updatedAt: "2024-02-20",
    },
    {
      id: 3,
      name: "Visa",
      type: "Card",
      status: "Active",
      description: "Visa credit and debit cards",
      usageCount: 456,
      createdAt: "2024-01-15",
      updatedAt: "2024-03-12",
    },
    {
      id: 4,
      name: "Mastercard",
      type: "Card",
      status: "Active",
      description: "Mastercard credit and debit cards",
      usageCount: 398,
      createdAt: "2024-01-15",
      updatedAt: "2024-03-12",
    },
    {
      id: 5,
      name: "American Express",
      type: "Card",
      status: "Active",
      description: "American Express cards",
      usageCount: 87,
      createdAt: "2024-01-20",
      updatedAt: "2024-03-01",
    },
    {
      id: 6,
      name: "Cash",
      type: "Cash",
      status: "Active",
      description: "Physical cash payments",
      usageCount: 23,
      createdAt: "2024-01-15",
      updatedAt: "2024-02-15",
    },
    {
      id: 7,
      name: "PayPal",
      type: "Bank",
      status: "Inactive",
      description: "PayPal online payment",
      usageCount: 12,
      createdAt: "2024-02-01",
      updatedAt: "2024-02-28",
    },
    {
      id: 8,
      name: "Wire Transfer (International)",
      type: "Bank",
      status: "Active",
      description: "International wire transfers (SWIFT)",
      usageCount: 34,
      createdAt: "2024-01-18",
      updatedAt: "2024-03-05",
    },
  ]);

  const loading = false;
  const error = null;
  const saveLoading = false;

  const getStatusBadgeClass = (status) => {
    const baseClass = styles.statusBadge;
    switch (status) {
      case "Active":
        return `${baseClass} ${styles.statusActive}`;
      case "Inactive":
        return `${baseClass} ${styles.statusInactive}`;
      default:
        return baseClass;
    }
  };

  const getTypeBadgeClass = (type) => {
    const baseClass = styles.typeBadge;
    switch (type) {
      case "Bank":
        return `${baseClass} ${styles.typeBank}`;
      case "Card":
        return `${baseClass} ${styles.typeCard}`;
      case "Cash":
        return `${baseClass} ${styles.typeCash}`;
      default:
        return baseClass;
    }
  };

  const filteredMethods = useMemo(() => {
    return paymentMethods.filter((method) => {
      const matchesSearch = search === "" ||
        method.name.toLowerCase().includes(search.toLowerCase()) ||
        method.description.toLowerCase().includes(search.toLowerCase());

      const matchesType = typeFilter === "" || method.type === typeFilter;
      const matchesStatus = statusFilter === "" || method.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [paymentMethods, search, typeFilter, statusFilter]);

  const activeMethods = filteredMethods.filter(m => m.status === "Active");
  const inactiveMethods = filteredMethods.filter(m => m.status === "Inactive");
  const totalUsage = filteredMethods.reduce((sum, m) => sum + m.usageCount, 0);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleAdd = () => {
    setMode("add");
    setSelectedMethod(null);
    setShowAddEditModal(true);
  };

  const handleEdit = (method) => {
    setMode("edit");
    setSelectedMethod(method);
    setShowAddEditModal(true);
  };

  const handleToggleStatus = (method) => {
    console.log(`Toggle status for payment method: ${method.id}`);
    // Implementation will update method status
  };

  const handleDelete = (method) => {
    if (method.usageCount > 0) {
      alert(`Cannot delete "${method.name}" - it has been used in ${method.usageCount} transactions.`);
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete "${method.name}"?`)) {
      console.log(`Delete payment method: ${method.id}`);
      // Implementation will delete method
    }
  };

  const handleSave = async (payload) => {
    try {
      if (mode === "add") {
        console.log("Add payment method:", payload);
      } else if (selectedMethod?.id) {
        console.log("Update payment method:", selectedMethod.id, payload);
      }
      setShowAddEditModal(false);
    } catch (err) {
      console.error("Failed to save payment method:", err);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.manager}>Payment Methods</div>
        <button className={styles.add} onClick={handleAdd}>
          + Add Payment Method
        </button>
      </div>

      <div className={styles.infoBar}>
        <span className={styles.infoIcon} aria-hidden>ℹ️</span>
        <span>
          Payment methods define how money flows in and out of SUNSKY. They are used in client payments, supplier payments, and refunds.
        </span>
      </div>

      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Active Methods</div>
          <div className={styles.summaryValue}>{activeMethods.length}</div>
          <div className={styles.summaryCount}>{totalUsage.toLocaleString()} total uses</div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Inactive Methods</div>
          <div className={styles.summaryValue} style={{ color: '#6b7280' }}>{inactiveMethods.length}</div>
          <div className={styles.summaryCount}>
            {inactiveMethods.reduce((sum, m) => sum + m.usageCount, 0).toLocaleString()} total uses
          </div>
        </div>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Total Methods</div>
          <div className={styles.summaryValue} style={{ color: '#3b82f6' }}>{filteredMethods.length}</div>
          <div className={styles.summaryCount}>of {paymentMethods.length} methods</div>
        </div>
      </div>

      <div className={styles.filterBar}>
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <input
            type="text"
            placeholder="Search by name or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className={styles.searchInput}
          />
          <button type="submit" className={styles.addbtn}>
            Search
          </button>
        </form>

        <SelectBox
          placeholder="Type: All"
          value={typeFilter}
          options={[
            { label: "All Types", value: "" },
            { label: "Bank", value: "Bank" },
            { label: "Card", value: "Card" },
            { label: "Cash", value: "Cash" },
          ]}
          onChange={(e) => setTypeFilter(e.target.value)}
        />

        <SelectBox
          placeholder="Status: All"
          value={statusFilter}
          options={[
            { label: "All Status", value: "" },
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
          ]}
          onChange={(e) => setStatusFilter(e.target.value)}
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading payment methods...</div>
      ) : (
        <>
          <div className={styles.meta}>
            Showing {filteredMethods.length} of {paymentMethods.length} payment methods
          </div>

          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>METHOD NAME</th>
                  <th>TYPE</th>
                  <th>DESCRIPTION</th>
                  <th>USAGE</th>
                  <th>STATUS</th>
                  <th>LAST UPDATED</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredMethods.map((method) => (
                  <tr key={method.id}>
                    <td>
                      <div className={styles.methodName}>{method.name}</div>
                    </td>
                    <td>
                      <span className={getTypeBadgeClass(method.type)}>
                        {method.type}
                      </span>
                    </td>
                    <td className={styles.description}>{method.description}</td>
                    <td>
                      <div className={styles.usageInfo}>
                        <div className={styles.usageCount}>{method.usageCount.toLocaleString()}</div>
                        <div className={styles.usageLabel}>transactions</div>
                      </div>
                    </td>
                    <td>
                      <span className={getStatusBadgeClass(method.status)}>
                        {method.status}
                      </span>
                    </td>
                    <td className={styles.date}>{method.updatedAt}</td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleEdit(method)}
                          title="Edit payment method"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className={styles.actionBtn}
                          onClick={() => handleToggleStatus(method)}
                          title={method.status === "Active" ? "Deactivate" : "Activate"}
                        >
                          {method.status === "Active" ? (
                            <PowerOff size={16} />
                          ) : (
                            <Power size={16} />
                          )}
                        </button>
                        <button
                          className={`${styles.actionBtn} ${method.usageCount > 0 ? styles.actionBtnDisabled : styles.actionBtnDanger}`}
                          onClick={() => handleDelete(method)}
                          title={method.usageCount > 0 ? "Cannot delete - method in use" : "Delete payment method"}
                          disabled={method.usageCount > 0}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <AddEditPaymentMethodModal
        open={showAddEditModal}
        onClose={() => setShowAddEditModal(false)}
        mode={mode}
        initialData={selectedMethod}
        onSave={handleSave}
        saveLoading={saveLoading}
      />
    </div>
  );
};

export default PaymentMethods;